import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import MarketComps from './MarketComps';
import FinancialsLedger from './FinancialsLedger';
import PropertyDocuments from './PropertyDocuments';
import PropertyLeases from './PropertyLeases';
import PropertyInsights from './PropertyInsights'; // New import
import Tooltip from './Tooltip';
import Tabs, { Tab } from './Tabs'; // New import, including Tab
import { showConfirm, showAlert } from '../utils/ModalService';


interface Property {
    id: string;
    custom_id?: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    property_type: string;
    sqft?: number;
    number_of_units: number;
    purchase_price: number;
    current_value: number;
    year_built: number;
    description?: string;
}

const PropertyDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [property, setProperty] = useState<Property | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;

        const fetchProperty = async () => {
            setLoading(true);
            try {
                const response = await api.get(`/properties/${id}`);
                setProperty(response.data);
            } catch (err) {
                console.error('Error fetching property details:', err);
                setError('Failed to load property details.');
            } finally {
                setLoading(false);
            }
        };

        fetchProperty();
    }, [id]);

    const handleDeleteProperty = async () => {
        if (!property) return;
        showConfirm('Confirm Action', `Are you sure you want to delete the property "${property.name}"? This action cannot be undone.`, async () => {
            try {
                await api.delete(`/properties/${property.id}`);
                showAlert('Property deleted successfully.');
                navigate('/properties'); // Redirect to properties list after deletion
            } catch (err: any) {
                console.error('Error deleting property:', err);
                setError(err.response?.data?.detail || 'Failed to delete property.');
            }
        });
    };

    if (loading) {
        return <div className="container mx-auto p-4">{t("propertyDetailView.k1")}</div>;
    }

    if (error || !property) {
        return <div className="container mx-auto p-4 text-danger">{t("propertyDetailView.k2")} {error || 'Property not found'}</div>;
    }

    return (
        <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
            <button 
                onClick={() => navigate('/properties')} 
                className="mb-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
                {t("propertyDetailView.k3")} </button>

            {/* Property Details Card */}
            <div className="bg-bg-secondary p-6 rounded-lg shadow border border-border-primary mb-8">
                <div className="flex justify-between items-start">
                    <div>
                        <div className="flex items-center">
                            <h1 className="text-3xl font-bold text-text-primary">{property.address}</h1>
                            <Tooltip text="This page provides a comprehensive overview of your property, including details, AI-extracted insights, documents, leases, financial records, and market comparables." />
                        </div>
                        {property.custom_id && (
                            <p className="text-lg text-text-secondary mt-2">{t("propertyDetailView.k4")} {property.custom_id}</p>
                        )}
                    </div>
                </div>
                <div className="mt-6 border-t border-border-primary pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div>
                            <p className="text-sm font-medium text-text-secondary">{property.custom_id ? t("propertyDetailView.k5") : t("propertyDetailView.k6")}</p>
                            <p className="text-xl font-semibold text-text-primary">{property.custom_id || property.id}</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k7")}</p>
                            <p className="text-xl font-semibold text-text-primary">{property.property_type}</p>
                        </div>
                        {property.sqft && (
                            <div>
                                <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k8")}</p>
                                <p className="text-xl font-semibold text-text-primary">{property.sqft.toLocaleString()} {t("propertyDetailView.k9")}</p>
                            </div>
                        )}
                        {property.number_of_units && (
                            <div>
                                <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k10")}</p>
                                <p className="text-xl font-semibold text-text-primary">{property.number_of_units}</p>
                            </div>
                        )}
                        {property.purchase_price && (
                            <div>
                                <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k11")}</p>
                                <p className="text-xl font-semibold text-text-primary">{property.purchase_price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                            </div>
                        )}
                        {property.current_value && (
                            <div>
                                <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k12")}</p>
                                <p className="text-xl font-semibold text-text-primary">{property.current_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                            </div>
                        )}
                        {property.year_built && (
                            <div>
                                <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k13")}</p>
                                <p className="text-xl font-semibold text-text-primary">{property.year_built}</p>
                            </div>
                        )}
                    </div>
                    {property.description && (
                        <div className="mt-6">
                            <p className="text-sm font-medium text-text-secondary">{t("propertyDetailView.k14")}</p>
                            <p className="text-base text-text-primary">{property.description}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* New Tabs Section */}
            <Tabs initialActiveTab="Financials">
                <Tab label="Financials">
                    <FinancialsLedger propertyId={property.id} />
                </Tab>
                <Tab label="Comparables">
                    <MarketComps propertyId={property.id} />
                </Tab>
                <Tab label="Property Insights">
                    <PropertyInsights propertyId={property.id} className="mb-8" />
                </Tab>
                <Tab label="Property Documents">
                    <PropertyDocuments propertyId={property.id} />
                </Tab>
                <Tab label="Property Leases">
                    <PropertyLeases propertyId={property.id} />
                </Tab>
            </Tabs>

        </div>
    );
};

export default PropertyDetailView;
